import React, { useState, useEffect } from 'react';
import { authAPI } from '../services/api';
import { activateOnKey } from '../utils/a11y';
import styles from '../styles/ProfileQuestion.module.css';

/**
 * ProfileQuestion - Inline chat component for cycle-triggered profile questions.
 *
 * Rendered when Claude includes a <ProfileQuestion:{...}> tag in a message.
 * Cycle activities trigger these to collect user profile data conversationally.
 *
 * Props (from component tag JSON):
 * @param {string} field - User profile field to update (moneyStage, confidence_score, etc.)
 * @param {string} question - Display question text
 * @param {string} type - Input type: selection-cards, checkbox-multiple, checkbox-single, quick-select-with-custom, slider
 * @param {Array} options - Answer choices (strings or {label, value} objects)
 * @param {boolean} allowCustom - Whether to show custom input option
 * @param {boolean} savesToProfile - true = save to up_users + history, false = history only
 * @param {number} min - Slider min value
 * @param {number} max - Slider max value
 * @param {object} labels - Slider labels keyed by value (e.g. {"1":"Not confident","10":"Very confident"})
 */
function ProfileQuestion({
  field,
  question,
  type = 'selection-cards',
  options = [],
  allowCustom = false,
  savesToProfile = true,
  min = 1,
  max = 10,
  labels = {}
}) {
  const [selected, setSelected] = useState(type === 'checkbox-multiple' ? [] : null);
  const [sliderValue, setSliderValue] = useState(Math.round((min + max) / 2));
  const [customValue, setCustomValue] = useState('');
  const [showCustom, setShowCustom] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [savedDisplay, setSavedDisplay] = useState('');
  const [error, setError] = useState(null);

  // Normalize options to {label, value} format
  const normalizedOptions = options.map(opt =>
    typeof opt === 'string' ? { label: opt, value: opt } : opt
  );

  // Names the radiogroup/group wrapping the answer choices, so a screen reader
  // announces the question before the first option.
  const questionId = `profile-question-${field}`;

  // Check if user already answered this field
  useEffect(() => {
    const checkExisting = async () => {
      try {
        const data = await authAPI.getCurrentUser();
        const user = data.user || data;
        if (user[field] && user[field] !== '') {
          setIsSaved(true);
          setSavedDisplay(user[field]);
        }
      } catch {
        // Ignore - user will just answer fresh
      }
    };
    // Only check for fields that save to profile
    if (savesToProfile) {
      checkExisting();
    }
  }, [field, savesToProfile]);

  const saveAnswer = async (value) => {
    setIsSaving(true);
    setError(null);
    try {
      const displayValue = Array.isArray(value) ? value.join(', ') : String(value);
      // Find the label for the selected value (for a nicer chat message)
      const selectedOpt = normalizedOptions.find(opt => opt.value === value);
      const sliderLabel = labels[String(value)];
      const displayLabel = selectedOpt ? selectedOpt.label
        : sliderLabel ? `${value} — ${sliderLabel}`
        : displayValue;

      await authAPI.saveProfileAnswer(field, displayValue, savesToProfile);
      setIsSaved(true);
      setSavedDisplay(displayLabel);

      // After animation completes, auto-send the answer as a chat message
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('profileQuestionAnswered', {
          detail: { field, value: displayValue, label: displayLabel }
        }));
      }, 800); // Wait for checkPop animation
    } catch (err) {
      console.error('[ProfileQuestion] Save failed:', err);
      setError('Could not save. Tap to retry.');
      setIsSaving(false);
    }
  };

  // Single select: save immediately on tap
  const handleSingleSelect = (value) => {
    if (isSaved || isSaving) return;
    setSelected(value);
    saveAnswer(value);
  };

  // Multi select: toggle selection
  const handleMultiToggle = (value) => {
    if (isSaved || isSaving) return;
    setSelected(prev =>
      prev.includes(value)
        ? prev.filter(v => v !== value)
        : [...prev, value]
    );
  };

  // Multi select: save all selected
  const handleMultiSave = () => {
    if (isSaved || isSaving) return;
    const allValues = [...selected];
    if (customValue.trim()) {
      allValues.push(customValue.trim());
    }
    saveAnswer(allValues.join(', '));
  };

  // Quick select with custom
  const handleQuickSelect = (value) => {
    if (isSaved || isSaving) return;
    setShowCustom(false);
    setSelected(value);
    saveAnswer(value);
  };

  const handleCustomSave = () => {
    if (isSaved || isSaving || !customValue.trim()) return;
    saveAnswer(customValue.trim());
  };

  // Slider: save on confirm
  const handleSliderSave = () => {
    if (isSaved || isSaving) return;
    saveAnswer(sliderValue);
  };

  // Get display label for slider value
  const getSliderLabel = (val) => {
    return labels[String(val)] || '';
  };

  // Saved state - show confirmation
  if (isSaved) {
    return (
      <div className={styles.card}>
        <div className={styles.savedCard}>
          <div className={styles.savedCheck}>&#10003;</div>
          <div className={styles.savedText}>{savedDisplay}</div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.card}>
      <div className={styles.header}>
        <span className={styles.badge}>Quick Question</span>
      </div>
      <h3 className={styles.question} id={questionId}>{question}</h3>

      {error && (
        <div
          className={styles.error}
          role="button"
          tabIndex={0}
          aria-label={`${error} Dismiss to try again.`}
          onClick={() => setError(null)}
          onKeyDown={activateOnKey(() => setError(null))}
        >
          {error}
        </div>
      )}

      {/* Selection Cards (single select) — one-of-many, so radiogroup/radio
          announces both the chosen option and the set position. */}
      {type === 'selection-cards' && (
        <div className={styles.optionsGrid} role="radiogroup" aria-labelledby={questionId}>
          {normalizedOptions.map((opt) => (
            <button
              key={opt.value}
              className={`${styles.optionButton} ${selected === opt.value ? styles.optionSelected : ''}`}
              role="radio"
              aria-checked={selected === opt.value}
              onClick={() => handleSingleSelect(opt.value)}
              disabled={isSaving}
            >
              {opt.label}
            </button>
          ))}
        </div>
      )}

      {/* Checkbox Single (radio-style) */}
      {type === 'checkbox-single' && (
        <div className={styles.optionsList} role="radiogroup" aria-labelledby={questionId}>
          {normalizedOptions.map((opt) => (
            <button
              key={opt.value}
              className={`${styles.optionButton} ${styles.optionFull} ${selected === opt.value ? styles.optionSelected : ''}`}
              role="radio"
              aria-checked={selected === opt.value}
              onClick={() => handleSingleSelect(opt.value)}
              disabled={isSaving}
            >
              {opt.label}
            </button>
          ))}
        </div>
      )}

      {/* Checkbox Multiple */}
      {type === 'checkbox-multiple' && (
        <>
          {/* Choose-all-that-apply, so each option is a checkbox. */}
          <div className={styles.optionsGrid} role="group" aria-labelledby={questionId}>
            {normalizedOptions.map((opt) => (
              <button
                key={opt.value}
                className={`${styles.optionButton} ${selected.includes(opt.value) ? styles.optionSelected : ''}`}
                role="checkbox"
                aria-checked={selected.includes(opt.value)}
                onClick={() => handleMultiToggle(opt.value)}
                disabled={isSaving}
              >
                {opt.label}
              </button>
            ))}
          </div>
          {allowCustom && (
            <input
              type="text"
              className={styles.customInput}
              placeholder="Other..."
              value={customValue}
              onChange={(e) => setCustomValue(e.target.value)}
              disabled={isSaving}
            />
          )}
          <button
            className={styles.saveButton}
            onClick={handleMultiSave}
            disabled={isSaving || (selected.length === 0 && !customValue.trim())}
          >
            {isSaving ? 'Saving...' : 'Save'}
          </button>
        </>
      )}

      {/* Quick Select with Custom */}
      {type === 'quick-select-with-custom' && (
        <>
          {/* Single select (plus a free-text escape hatch below the group). */}
          <div className={styles.optionsGrid} role="radiogroup" aria-labelledby={questionId}>
            {normalizedOptions.map((opt) => (
              <button
                key={opt.value}
                className={`${styles.optionButton} ${selected === opt.value ? styles.optionSelected : ''}`}
                role="radio"
                aria-checked={selected === opt.value}
                onClick={() => handleQuickSelect(opt.value)}
                disabled={isSaving}
              >
                {opt.label}
              </button>
            ))}
          </div>
          {allowCustom && !showCustom && (
            <button
              className={styles.customToggle}
              onClick={() => setShowCustom(true)}
              disabled={isSaving}
            >
              + Something else
            </button>
          )}
          {showCustom && (
            <div className={styles.customRow}>
              <input
                type="text"
                className={styles.customInput}
                placeholder="Type your preference..."
                value={customValue}
                onChange={(e) => setCustomValue(e.target.value)}
                autoFocus
                disabled={isSaving}
                onKeyDown={(e) => e.key === 'Enter' && handleCustomSave()}
              />
              <button
                className={styles.saveButton}
                onClick={handleCustomSave}
                disabled={isSaving || !customValue.trim()}
              >
                {isSaving ? '...' : '\u2192'}
              </button>
            </div>
          )}
        </>
      )}

      {/* Slider (1-10 scale) */}
      {type === 'slider' && (
        <div className={styles.sliderContainer}>
          <div className={styles.sliderLabels}>
            <span className={styles.sliderLabelLeft}>{getSliderLabel(min) || min}</span>
            <span className={styles.sliderLabelRight}>{getSliderLabel(max) || max}</span>
          </div>
          <input
            type="range"
            className={styles.slider}
            min={min}
            max={max}
            step={1}
            value={sliderValue}
            onChange={(e) => setSliderValue(Number(e.target.value))}
            disabled={isSaving}
          />
          <div className={styles.sliderValue}>{sliderValue}</div>
          {getSliderLabel(sliderValue) && (
            <div className={styles.sliderCurrentLabel}>{getSliderLabel(sliderValue)}</div>
          )}
          <button
            className={styles.saveButton}
            onClick={handleSliderSave}
            disabled={isSaving}
          >
            {isSaving ? 'Saving...' : 'Confirm'}
          </button>
        </div>
      )}

      {isSaving && <div className={styles.savingOverlay}>Saving...</div>}
    </div>
  );
}

export default ProfileQuestion;
